/* ---------------------------------------------------------------------------
   ACCOUNT ENTRY AND REGISTRATION

   Three behaviours, all of which degrade to something usable if the app proxy
   is unreachable:

     1. Email step   -> ask whether the address has an account, then show the
                        password step or send the shopper to registration.
     2. Registration -> confirm the two passwords match before Shopify sees them.
     3. Birthday     -> Shopify's create_customer form cannot carry a metafield,
                        so the month and day are held across the redirect and
                        saved once the new account exists.
   --------------------------------------------------------------------------- */
(function () {
  const BIRTHDAY_STASH = 'synergy:pending-birthday';

  const emailLooksValid = (value) => {
    const email = String(value || '').trim();
    const at = email.indexOf('@');
    const dot = email.lastIndexOf('.');
    return (
      email.length > 2 &&
      email.length <= 254 &&
      at > 0 &&
      dot > at + 1 &&
      dot < email.length - 1 &&
      email.indexOf(' ') === -1 &&
      email.indexOf('@', at + 1) === -1
    );
  };

  /* ---------- step 1 -> step 2, or off to registration ---------- */
  class SynergyAccountEntry extends HTMLElement {
    connectedCallback() {
      this.emailStep = this.querySelector('[data-step="email"]');
      this.passwordStep = this.querySelector('[data-step="password"]');
      this.recoverStep = this.querySelector('[data-step="recover"]');
      this.input = this.querySelector('#syn-acct-email');
      this.loginEmail = this.querySelector('[data-login-email]');
      this.error = this.querySelector('[data-email-error]');
      this.button = this.querySelector('[data-continue]');
      this.registerUrl = this.dataset.registerUrl || '/account/register';
      this.lookup = this.dataset.lookup || '/apps/wishlist/account-exists';

      this.button.addEventListener('click', () => this.onContinue());
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); this.onContinue(); }
      });
      this.input.addEventListener('input', () => this.clearError());

      /* A rejected sign-in reloads this page with Shopify's error rendered in
         the password step. Starting at step 1 would hide it and look as though
         nothing had happened. */
      if (this.querySelector('.syn-acct__form-error')) {
        this.showPasswordStep(this.rememberedEmail());
      }

      /* Shopify has no page at /account/recover, so the reset form lives here
         and is reached by hash. Landing on #recover, or coming back from a
         successful send, opens it directly. */
      const toRecover = this.querySelector('[data-show-recover]');
      if (toRecover) {
        toRecover.addEventListener('click', () => this.showRecoverStep());
      }
      const backToSignIn = this.querySelector('[data-show-signin]');
      if (backToSignIn) {
        backToSignIn.addEventListener('click', (e) => {
          e.preventDefault();
          this.showEmailStep();
        });
      }
      if (window.location.hash === '#recover' || this.querySelector('[data-step="recover"] .syn-acct__hint') && this.dataset.recoverPosted === 'true') {
        this.showRecoverStep();
      }
    }

    rememberedEmail() {
      try { return sessionStorage.getItem('synergy:account-email') || ''; } catch (e) { return ''; }
    }

    remember(email) {
      try { sessionStorage.setItem('synergy:account-email', email); } catch (e) { /* private mode */ }
    }

    clearError() {
      this.error.hidden = true;
      this.error.textContent = '';
    }

    say(message) {
      this.error.textContent = message;
      this.error.hidden = false;
    }

    showRecoverStep() {
      if (!this.recoverStep) return;
      this.emailStep.hidden = true;
      this.passwordStep.hidden = true;
      this.recoverStep.hidden = false;
      const field = this.querySelector('[data-recover-email]');
      if (field) {
        if (!field.value) field.value = this.rememberedEmail();
        field.focus();
      }
    }

    showEmailStep() {
      if (this.recoverStep) this.recoverStep.hidden = true;
      this.passwordStep.hidden = true;
      this.emailStep.hidden = false;
      if (this.input) this.input.focus();
    }

    showPasswordStep(email) {
      if (email) this.loginEmail.value = email;
      this.emailStep.hidden = true;
      this.passwordStep.hidden = false;
      const pw = this.querySelector('#syn-acct-password');
      if (pw && !this.querySelector('.syn-acct__form-error')) pw.focus();
    }

    async onContinue() {
      const email = this.input.value.trim();
      if (!emailLooksValid(email)) {
        this.say('Enter a valid email address.');
        this.input.focus();
        return;
      }

      this.remember(email);
      this.button.disabled = true;
      this.button.classList.add('is-busy');

      let exists = null;
      try {
        const res = await fetch(this.lookup, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        if (res.ok) {
          const data = await res.json();
          exists = typeof data.exists === 'boolean' ? data.exists : null;
        }
      } catch (e) {
        exists = null;              // treated as unknown below
      }

      this.button.disabled = false;
      this.button.classList.remove('is-busy');

      if (exists === false) {
        /* Carry the address so it is not typed twice. */
        const sep = this.registerUrl.indexOf('?') === -1 ? '?' : '&';
        window.location.href = this.registerUrl + sep + 'email=' + encodeURIComponent(email);
        return;
      }

      /* true, or unknown because the lookup failed: the password step carries a
         create-account link, so it is the safe answer either way. */
      this.showPasswordStep(email);

      const link = this.querySelector('[data-register-link]');
      if (link) {
        const sep = link.getAttribute('href').indexOf('?') === -1 ? '?' : '&';
        link.setAttribute('href', link.getAttribute('href') + sep + 'email=' + encodeURIComponent(email));
      }
    }
  }

  /* ---------- registration ---------- */
  class SynergyAccountRegister extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.email = this.querySelector('[data-register-email]');
      this.password = this.querySelector('[data-password]');
      this.confirm = this.querySelector('[data-password-confirm]');
      this.error = this.querySelector('[data-password-error]');
      this.month = this.querySelector('[data-birth-month]');
      this.day = this.querySelector('[data-birth-day]');

      this.prefillEmail();
      if (this.month && this.day) this.month.addEventListener('change', () => this.trimDays());

      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    prefillEmail() {
      if (!this.email || this.email.value) return;
      let fromQuery = '';
      try {
        fromQuery = new URLSearchParams(window.location.search).get('email') || '';
      } catch (e) { /* ignore */ }
      let remembered = '';
      try { remembered = sessionStorage.getItem('synergy:account-email') || ''; } catch (e) { /* ignore */ }
      const value = fromQuery || remembered;
      if (value && emailLooksValid(value)) this.email.value = value;
    }

    /* February never has 31 days; a shopper should not be able to pick one. */
    trimDays() {
      const lengths = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      const month = Number(this.month.value);
      const max = month >= 1 && month <= 12 ? lengths[month - 1] : 31;
      const chosen = Number(this.day.value);
      [...this.day.options].forEach((opt) => {
        const n = Number(opt.value);
        if (n) opt.hidden = n > max;
      });
      if (chosen > max) this.day.value = '';
    }

    onSubmit(event) {
      if (this.password && this.confirm && this.password.value !== this.confirm.value) {
        event.preventDefault();
        this.error.textContent = 'Those passwords do not match.';
        this.error.hidden = false;
        this.confirm.focus();
        return;
      }
      if (this.error) this.error.hidden = true;

      /* The account does not exist yet, so the birthday cannot be saved here.
         Hold it across the redirect and write it once Shopify has created the
         customer and signed them in. */
      if (this.month && this.day && this.month.value && this.day.value) {
        try {
          sessionStorage.setItem(BIRTHDAY_STASH, JSON.stringify({
            month: Number(this.month.value),
            day: Number(this.day.value),
            path: this.dataset.birthdayPath || '/apps/wishlist/birthday',
          }));
        } catch (e) { /* private mode: the birthday is simply not saved */ }
      }
    }
  }

  /* ---------- save a held birthday, once the account exists ---------- */
  async function flushBirthday() {
    let pending = null;
    try {
      const raw = sessionStorage.getItem(BIRTHDAY_STASH);
      if (!raw) return;
      pending = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!pending || !pending.month || !pending.day) return;

    /* Only attempt it while signed in -- the endpoint identifies the customer
       from Shopify's signed proxy parameters, not from anything we send. */
    const config = window.SynergyWishlistConfig || {};
    if (!config.signedIn) return;

    try {
      const res = await fetch(pending.path || '/apps/wishlist/birthday', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month: pending.month, day: pending.day }),
      });
      if (res.ok) sessionStorage.removeItem(BIRTHDAY_STASH);
    } catch (e) {
      /* left in place; the next signed-in page load tries again */
    }
  }

  if (!customElements.get('synergy-account-entry')) {
    customElements.define('synergy-account-entry', SynergyAccountEntry);
  }
  if (!customElements.get('synergy-account-register')) {
    customElements.define('synergy-account-register', SynergyAccountRegister);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', flushBirthday);
  } else {
    flushBirthday();
  }
})();
